"use client";

import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Bot, Cpu, ArrowRight, Sparkles, Layers, Zap, Network, Workflow, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
interface EcosystemNode {
  id: string;
  icon: React.ElementType;
  title: {
    en: string;
    vi: string;
  };
  desc: {
    en: string;
    vi: string;
  };
  link: string;
  badge?: {
    en: string;
    vi: string;
  };
}
const ECOSYSTEM_BRANCHES: EcosystemNode[] = [{
  id: "lego-robotics",
  icon: Bot,
  title: {
    en: "LEGO Robotics",
    vi: "Robot LEGO Thực Hành"
  },
  desc: {
    en: "Hands-on engineering, creative mechanisms, and early coding through LEGO Education systems.",
    vi: "Lắp ráp kỹ thuật, tư duy cơ chế sáng tạo và lập trình trực quan cùng các hệ LEGO Education."
  },
  link: "/ProgramsPage",
  badge: {
    en: "Hands-on Track",
    vi: "Học Phần Thực Hành"
  }
}, {
  id: "robotics-pathway",
  icon: Cpu,
  title: {
    en: "Robotics Learning Pathway",
    vi: "Lộ Trình Học Robot"
  },
  desc: {
    en: "Comprehensive progression spanning Duplo foundations, WhalesBot engineering, and tournament readiness.",
    vi: "Lộ trình đào tạo hoàn chỉnh từ nền tảng Duplo, kỹ thuật WhalesBot đến đấu trường thực chiến."
  },
  link: "/RoboticsLearningPathwayPage",
  badge: {
    en: "Core Pathway",
    vi: "Lộ Trình Trọng Tâm"
  }
}];
const ECOSYSTEM_FLOW_STEPS = [{
  num: "01",
  title: {
    en: "Duplo D1–D4 Foundation",
    vi: "Nền Tảng Duplo D1–D4"
  },
  tag: {
    en: "Ages 4–7",
    vi: "4–7 Tuổi"
  },
  focus: {
    en: "Early mechanical builds, physical sensors, tactile logic & foundational STEM concepts",
    vi: "Cơ cấu lắp ráp sơ cấp, cảm biến vật lý, tư duy logic trực quan và khái niệm STEM nền tảng"
  }
}, {
  num: "02",
  title: {
    en: "WhalesBot HP1–HP6 Progression",
    vi: "Kỹ Thuật WhalesBot HP1–HP6"
  },
  tag: {
    en: "Ages 7–13",
    vi: "7–13 Tuổi"
  },
  focus: {
    en: "Coding cards, Scratch block logic, smart controller integration & advanced electromechanics",
    vi: "Lập trình thẻ lệnh, khối mã Scratch, tích hợp bộ điều khiển thông minh & cơ điện tử nâng cao"
  }
}, {
  num: "03",
  title: {
    en: "ENJOY AI Tournament Preparation",
    vi: "Chuẩn Bị Thi Đấu ENJOY AI"
  },
  tag: {
    en: "Ages 8–16",
    vi: "8–16 Tuổi"
  },
  focus: {
    en: "Autonomous field missions, algorithmic speed routing, competition strategy & teamwork",
    vi: "Nhiệm vụ tự hành sa bàn, thuật toán tối ưu lộ trình, chiến thuật thi đấu & kỹ năng đồng đội"
  }
}];
export default function EcosystemCopy() {
  const [lang, setLang] = useState<"en" | "vi">("vi");
  useEffect(() => {
    const getStoredLang = (): "en" | "vi" => {
      try {
        const stored = localStorage.getItem("roboto_lang");
        if (stored === "en" || stored === "vi") return stored;
      } catch {
        // Fallback to default
      }
      return "vi";
    };
    setLang(getStoredLang());
    const handleLangChange = (e: Event) => {
      const customEv = e as CustomEvent;
      if (customEv.detail && (customEv.detail === "en" || customEv.detail === "vi")) {
        setLang(customEv.detail);
      } else {
        setLang(getStoredLang());
      }
    };
    window.addEventListener("roboto-lang-change", handleLangChange);
    window.addEventListener("storage", handleLangChange);
    return () => {
      window.removeEventListener("roboto-lang-change", handleLangChange);
      window.removeEventListener("storage", handleLangChange);
    };
  }, []);
  const content = {
    eyebrow: {
      en: "CURRENT ROBOTICS OFFERINGS",
      vi: "CHƯƠNG TRÌNH ĐÀO TẠO HIỆN HÀNH"
    },
    title: {
      en: "LEGO Robotics & The Robotics Pathway",
      vi: "Robot LEGO & Lộ Trình Học Robot"
    },
    subtitle: {
      en: "Structured engineering curriculum built on authentic hardware kits and clear milestone progressions for ages 4 to 16.",
      vi: "Chương trình kỹ thuật chuẩn mực xây dựng trên bộ giáo cụ chuyên dụng và lộ trình từng bước rõ ràng cho lứa tuổi 4 đến 16."
    },
    centerHub: {
      en: "ROBOTO HUBS",
      vi: "TRUNG TÂM ROBOTO"
    },
    centerSub: {
      en: "Hardware & Pathway System",
      vi: "Hệ Thống Thiết Bị & Lộ Trình"
    },
    viewLink: {
      en: "View Details",
      vi: "Xem Chi Tiết"
    },
    diagramHeader: {
      en: "ROBOTICS PATHWAY PIPELINE",
      vi: "TIẾN TRÌNH LỘ TRÌNH ROBOT"
    },
    diagramDesc: {
      en: "From early mechanical intuition in Duplo D1–D4 to WhalesBot HP1–HP6 mastery and ENJOY AI tournament arenas.",
      vi: "Từ trực giác cơ khí ban đầu với Duplo D1–D4 tới làm chủ WhalesBot HP1–HP6 và tiến bước vào đấu trường ENJOY AI."
    },
    systemLabel: {
      en: "ROBOTICS PATHWAY ARCHITECTURE // V4.2",
      vi: "KIẾN TRÚC LỘ TRÌNH ROBOT // V4.2"
    }
  };
  const legoNode = ECOSYSTEM_BRANCHES[0];
  const pathwayNode = ECOSYSTEM_BRANCHES[1];
  const LegoIcon = legoNode.icon;
  const PathwayIcon = pathwayNode.icon;
  return <section id="programecosystem" data-nav="light" className="relative overflow-hidden bg-background text-foreground py-20 lg:py-28">
      {/* Target anchor fallback for secondary syntax */}
      <div id="program-ecosystem" aria-hidden="true" className="absolute top-0 left-0" />

      {/* Blueprint Spec-Rail Accent Grid Layer */}
      <div className="pointer-events-none absolute inset-0 z-0 opacity-25" aria-hidden="true">
        <div className="h-full w-full" style={{
        backgroundImage: `
              linear-gradient(to right, var(--border) 1px, transparent 1px),
              linear-gradient(to bottom, var(--border) 1px, transparent 1px)
            `,
        backgroundSize: "40px 40px"
      }} />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-7xl h-full border-x border-border/60" />
      </div>

      {/* Spec-rail Glowing Accents */}
      <div className="pointer-events-none absolute -top-24 left-1/2 -translate-x-1/2 size-96 rounded-full bg-secondary/15 blur-3xl z-0" aria-hidden="true" />
      <div className="pointer-events-none absolute -bottom-24 right-10 size-80 rounded-full bg-accent/15 blur-3xl z-0" aria-hidden="true" />

      <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16 lg:mb-20">
          <motion.div initial={{
          opacity: 0,
          y: 12
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4
        }} className="inline-flex items-center gap-2 px-3 py-1 rounded-lg bg-muted border border-border text-xs font-mono tracking-wider text-muted-foreground uppercase mb-4">
            <Layers className="size-3.5 text-secondary" />
            <span>{content.eyebrow[lang]}</span>
          </motion.div>

          <motion.h2 initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground mb-4">
            {content.title[lang]}
          </motion.h2>

          <motion.p initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="text-base sm:text-lg text-muted-foreground font-sans leading-relaxed">
            {content.subtitle[lang]}
          </motion.p>
        </div>

        {/* Hub and Spoke Diagram Container - 2 Items Only */}
        <div className="relative max-w-5xl mx-auto mb-16 lg:mb-24">
          {/* Desktop Central Node & Spoke Layout (1 item on left, 1 item on right) */}
          <div className="hidden lg:grid grid-cols-12 gap-8 items-center">
            {/* Left Node: LEGO Robotics */}
            <div className="col-span-4">
              <motion.div initial={{
              opacity: 0,
              x: -30
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5
            }} data-index={0} className="group relative">
                <div className="relative z-10 bg-card border border-border rounded-xl p-6 shadow-sm hover:shadow-md hover:border-secondary transition-[box-shadow,border-color,transform,background-color] duration-300 hover:-translate-y-1">
                  {/* Spec line connector tag to central hub */}
                  <div className="absolute top-1/2 -right-8 w-8 h-[2px] bg-border group-hover:bg-secondary transition-colors" />

                  <div className="flex items-start gap-4">
                    <div className="p-3 rounded-lg bg-muted text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors shrink-0">
                      <LegoIcon className="size-6" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-serif font-bold text-lg text-foreground group-hover:text-primary transition-colors">
                          {legoNode.title[lang]}
                        </h3>
                        {legoNode.badge && <Badge variant="secondary" className="text-[10px] py-0 px-1.5">
                            {legoNode.badge[lang]}
                          </Badge>}
                      </div>
                      <p className="text-sm text-muted-foreground line-clamp-3 mb-3">
                        {legoNode.desc[lang]}
                      </p>
                      <Link to={legoNode.link} className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:text-primary/80 transition-colors">
                        <span>{content.viewLink[lang]}</span>
                        <ArrowRight className="size-3.5 transition-transform group-hover:translate-x-1" />
                      </Link>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Central ROBOTO Hub Node */}
            <div className="col-span-4 flex justify-center py-4">
              <motion.div initial={{
              opacity: 0,
              scale: 0.85
            }} whileInView={{
              opacity: 1,
              scale: 1
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.15
            }} className="relative flex flex-col items-center justify-center size-64 rounded-full bg-gradient-to-br from-card via-background to-muted border-2 border-secondary shadow-xl text-center p-6">
                {/* Radial ring effect */}
                <div className="absolute -inset-3 rounded-full border border-dashed border-secondary/40 animate-spin-slow pointer-events-none" />
                <div className="absolute -inset-6 rounded-full border border-border/50 pointer-events-none" />

                <div className="p-3 rounded-full bg-primary/10 text-primary mb-2">
                  <Sparkles className="size-8" />
                </div>

                <div className="font-serif text-2xl font-black tracking-wider text-foreground">
                  ROBOTO
                </div>

                <div className="text-xs font-mono uppercase tracking-widest text-secondary font-bold mt-1">
                  {content.centerHub[lang]}
                </div>

                <p className="text-[11px] text-muted-foreground mt-2 max-w-[150px]">
                  {content.centerSub[lang]}
                </p>
              </motion.div>
            </div>

            {/* Right Node: Robotics Pathway */}
            <div className="col-span-4">
              <motion.div initial={{
              opacity: 0,
              x: 30
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.1
            }} data-index={1} className="group relative">
                <div className="relative z-10 bg-card border border-border rounded-xl p-6 shadow-sm hover:shadow-md hover:border-secondary transition-[box-shadow,border-color,transform,background-color] duration-300 hover:-translate-y-1">
                  {/* Spec line connector tag to central hub */}
                  <div className="absolute top-1/2 -left-8 w-8 h-[2px] bg-border group-hover:bg-secondary transition-colors" />

                  <div className="flex items-start gap-4">
                    <div className="p-3 rounded-lg bg-muted text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors shrink-0">
                      <PathwayIcon className="size-6" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-serif font-bold text-lg text-foreground group-hover:text-primary transition-colors">
                          {pathwayNode.title[lang]}
                        </h3>
                        {pathwayNode.badge && <Badge variant="secondary" className="text-[10px] py-0 px-1.5">
                            {pathwayNode.badge[lang]}
                          </Badge>}
                      </div>
                      <p className="text-sm text-muted-foreground line-clamp-3 mb-3">
                        {pathwayNode.desc[lang]}
                      </p>
                      <Link to={pathwayNode.link} className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:text-primary/80 transition-colors">
                        <span>{content.viewLink[lang]}</span>
                        <ArrowRight className="size-3.5 transition-transform group-hover:translate-x-1" />
                      </Link>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>

          {/* Mobile & Tablet Layout */}
          <div className="lg:hidden flex flex-col gap-6">
            {/* Mobile Hub Header */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="bg-card border-2 border-secondary rounded-2xl p-6 text-center shadow-md relative overflow-hidden">
              <div className="inline-flex p-3 rounded-full bg-primary/10 text-primary mb-3">
                <Sparkles className="size-7" />
              </div>
              <h3 className="font-serif text-2xl font-bold text-foreground">
                ROBOTO
              </h3>
              <p className="text-xs font-mono uppercase text-secondary font-bold tracking-widest mt-1">
                {content.centerHub[lang]}
              </p>
              <p className="text-xs text-muted-foreground mt-2">
                {content.centerSub[lang]}
              </p>
            </motion.div>

            {/* Mobile Cards with vertical connector rail */}
            <div className="relative pl-6 border-l-2 border-border/80 flex flex-col gap-6 ml-4">
              {ECOSYSTEM_BRANCHES.map((branch, idx) => {
              const IconComp = branch.icon;
              return <motion.div key={branch.id} initial={{
                opacity: 0,
                y: 20
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.4,
                delay: idx * 0.1
              }} data-index={idx} className="relative group">
                    {/* Rail connection point */}
                    <div className="absolute -left-[31px] top-6 size-4 rounded-full bg-card border-2 border-secondary group-hover:bg-secondary transition-colors" />

                    <div className="bg-card border border-border rounded-xl p-5 shadow-xs hover:border-secondary transition-all">
                      <div className="flex items-start gap-4">
                        <div className="p-2.5 rounded-lg bg-muted text-primary shrink-0">
                          <IconComp className="size-5" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <h3 className="font-serif font-bold text-base text-foreground">
                              {branch.title[lang]}
                            </h3>
                            {branch.badge && <Badge variant="secondary" className="text-[10px] py-0 px-1.5">
                                {branch.badge[lang]}
                              </Badge>}
                          </div>
                          <p className="text-xs text-muted-foreground mb-3 leading-relaxed">
                            {branch.desc[lang]}
                          </p>
                          <Button size="sm" variant="outline" asChild className="h-8 text-xs">
                            <Link to={branch.link}>
                              <span>{content.viewLink[lang]}</span>
                              <ArrowRight className="size-3.5" />
                            </Link>
                          </Button>
                        </div>
                      </div>
                    </div>
                  </motion.div>;
            })}
            </div>
          </div>
        </div>

        {/* Visual Ecosystem Architecture Diagram & Technical Photograph Container */}
        <motion.div initial={{
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="max-w-6xl mx-auto rounded-2xl bg-[#070E20] border border-cyan-500/20 shadow-2xl overflow-hidden text-white relative">
          {/* Circuit / Spec Rail Backdrop Grid */}
          <div className="absolute inset-0 pointer-events-none opacity-15" style={{
          backgroundImage: `
                linear-gradient(to right, #00f2fe 1px, transparent 1px),
                linear-gradient(to bottom, #00f2fe 1px, transparent 1px)
              `,
          backgroundSize: "32px 32px"
        }} aria-hidden="true" />

          {/* Top Technical Control Bar */}
          <div className="relative z-10 flex flex-wrap items-center justify-between gap-4 px-6 py-4 bg-[#0A1329] border-b border-white/10">
            <div className="flex items-center gap-2">
              <div className="size-2.5 rounded-full bg-cyan-400 animate-pulse" />
              <span className="font-mono text-xs text-cyan-300 font-semibold tracking-wider uppercase">
                {content.systemLabel[lang]}
              </span>
            </div>
            <div className="flex items-center gap-4 text-xs text-slate-400 font-mono">
              <span className="hidden sm:inline-flex items-center gap-1.5">
                <Workflow className="size-3.5 text-secondary" />
                <span>Progressive Progression Stages</span>
              </span>
              <span className="inline-flex items-center gap-1.5 text-cyan-400 font-bold">
                <Zap className="size-3.5" />
                <span>Direct Pathway Pipeline</span>
              </span>
            </div>
          </div>

          <div className="relative z-10 p-6 sm:p-8 lg:p-10 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            {/* Left Graphic Photo & Visual Overlay */}
            <div className="lg:col-span-6 relative group rounded-xl overflow-hidden border border-white/10 bg-slate-900">
              <div className="aspect-[4/3] w-full relative overflow-hidden">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/roboto_ecosystem_lab_diagram.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/roboto_ecosystem_lab_diagram.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/roboto_ecosystem_lab_diagram.png-wvc-sizes" alt="ROBOTO Academy robotics learning pathway stages and hardware diagram" className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                {/* Technical Deep Blue Scrim Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#070E20] via-[#070E20]/40 to-transparent" />

                {/* Technical Node Badges Overlaid on Photograph */}
                <div className="absolute top-4 left-4 inline-flex items-center gap-1.5 px-3 py-1 rounded-md bg-[#070E20]/90 border border-cyan-400/40 text-[11px] font-mono text-cyan-300 shadow-md">
                  <Network className="size-3.5 text-secondary" />
                  <span>ROBOTICS PATHWAY PIPELINE</span>
                </div>

                <div className="absolute bottom-4 left-4 right-4 p-4 rounded-lg bg-[#0A1329]/90 border border-white/10 backdrop-blur-md">
                  <p className="font-serif font-bold text-sm text-white mb-1">
                    {content.diagramHeader[lang]}
                  </p>
                  <p className="text-xs text-slate-300 font-sans leading-relaxed">
                    {content.diagramDesc[lang]}
                  </p>
                </div>
              </div>
            </div>

            {/* Right Flow Step Pipeline - Duplo D1-D4 -> WhalesBot HP1-HP6 -> ENJOY AI */}
            <div className="lg:col-span-6 flex flex-col gap-3.5">
              {ECOSYSTEM_FLOW_STEPS.map((step, idx) => <div key={step.num} data-index={idx} className="group relative flex items-start sm:items-center gap-4 p-4 rounded-xl bg-white/5 border border-white/10 hover:border-cyan-400/50 hover:bg-white/10 transition-all duration-300">
                  <div className="flex items-center justify-center size-10 rounded-lg bg-cyan-950 border border-cyan-400/30 text-cyan-300 font-mono text-sm font-bold shrink-0 mt-0.5 sm:mt-0">
                    {step.num}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 justify-between mb-1 flex-wrap">
                      <h4 className="font-serif text-sm font-bold text-white group-hover:text-cyan-300 transition-colors">
                        {step.title[lang]}
                      </h4>
                      <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-secondary/20 text-secondary border border-secondary/30 shrink-0">
                        {step.tag[lang]}
                      </span>
                    </div>
                    <p className="text-xs text-slate-300 font-sans leading-relaxed">
                      {step.focus[lang]}
                    </p>
                  </div>
                  <CheckCircle2 className="size-4 text-cyan-400/60 group-hover:text-cyan-300 shrink-0 transition-colors mt-1 sm:mt-0" />
                </div>)}
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}